import { useEffect, useState } from 'react';
import { CheckIcon, PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface EditableHeaderTitleProps {
  title: string;
  fallbackTitle: string;
  onSave?: (title: string) => Promise<void> | void;
  isSaving?: boolean;
  titleClassName?: string;
  buttonClassName?: string;
  inputClassName?: string;
  maxWidthClassName?: string;
}

export default function EditableHeaderTitle({
  title,
  fallbackTitle,
  onSave,
  isSaving = false,
  titleClassName = 'text-[15px] font-semibold text-gray-50',
  buttonClassName = '',
  inputClassName = '',
  maxWidthClassName = 'max-w-[320px]',
}: EditableHeaderTitleProps) {
  const displayTitle = title || fallbackTitle;
  const [isEditing, setIsEditing] = useState(false);
  const [editingTitle, setEditingTitle] = useState(displayTitle);

  useEffect(() => {
    if (!isEditing) {
      setEditingTitle(displayTitle);
    }
  }, [displayTitle, isEditing]);

  const startEdit = () => {
    if (!onSave || isSaving) return;
    setEditingTitle(displayTitle);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setEditingTitle(displayTitle);
    setIsEditing(false);
  };

  const saveEdit = async () => {
    const nextTitle = editingTitle.trim();
    if (!nextTitle) {
      cancelEdit();
      return;
    }

    if (nextTitle !== displayTitle) {
      await onSave?.(nextTitle);
    }

    setIsEditing(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveEdit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelEdit();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={editingTitle}
          onChange={(event) => setEditingTitle(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={saveEdit}
          disabled={isSaving}
          className={`h-9 w-[280px] max-w-[38vw] rounded-lg border border-primary/60 bg-bg-elevated px-3 text-[15px] font-semibold text-gray-50 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-60 ${inputClassName}`}
          autoFocus
        />
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={saveEdit}
          disabled={isSaving}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-primary hover:bg-primary/10 disabled:opacity-50"
          aria-label="保存标题"
          title="保存标题"
        >
          <CheckIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={cancelEdit}
          disabled={isSaving}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-bg-elevated hover:text-gray-100 disabled:opacity-50"
          aria-label="取消编辑标题"
          title="取消编辑标题"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      disabled={!onSave}
      className={`group -ml-1 flex min-w-0 items-center gap-2 rounded-lg border border-transparent px-2 py-1.5 text-left transition-colors hover:border-border-subtle hover:bg-bg-elevated focus-visible:border-primary focus-visible:bg-bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:cursor-default disabled:hover:border-transparent disabled:hover:bg-transparent ${maxWidthClassName} ${buttonClassName}`}
      title={onSave ? '点击修改标题' : displayTitle}
      aria-label={onSave ? '点击修改标题' : undefined}
    >
      <span className={`truncate group-hover:text-primary ${titleClassName}`}>
        {displayTitle}
      </span>
      {onSave && (
        <PencilSquareIcon className="h-4 w-4 flex-shrink-0 text-gray-500 transition-colors group-hover:text-primary" />
      )}
    </button>
  );
}
